/**
 * Layout rules of the command surface, kept pure so they are unit tested:
 * where the detail panel goes (side column or bottom sheet), how the command
 * strip expands (in place or as an overlay), the bottom sheet's three heights
 * and its drag snapping, and when the Loader HUD folds its secondary scene
 * actions into an overflow menu or turns its scene controls vertical.
 * Decisions are recorded in LAYOUT_NOTES.md next to this file.
 */

/** The mode tabs control the stage panel: its id, and each tab's id for aria-labelledby. */
export const MODE_PANEL_ID = 'mode-panel';
// Old Operations/Analysis deep links use the Loader navigation tab.
export const modeTabId = (mode: string) => `mode-tab-${mode === 'operations' || mode === 'analysis' ? 'loader' : mode}`;

/** Below this viewport width the detail panel is a bottom sheet and the strip an overlay (--breakpoint-desk in globals.css). */
export const SHEET_BELOW = 1100;

export type DetailPresentation = 'side' | 'sheet';
export type StripPresentation = 'rail' | 'overlay';

export function detailPresentation(viewportWidth: number): DetailPresentation {
  return viewportWidth < SHEET_BELOW ? 'sheet' : 'side';
}

/** Rail: 64 px icons that expand over the stage on hover and push the stage when pinned. Overlay: icons only in the layout, the list always floats. */
export function stripPresentation(viewportWidth: number): StripPresentation {
  return viewportWidth < SHEET_BELOW ? 'overlay' : 'rail';
}

/** Toasts: under the top bar on desk widths; below 1100 px in the stage's bottom-right corner, above the bottom panel and the sheet. */
export function toastPlacement(viewportWidth: number): 'top-right' | 'stage' {
  return viewportWidth < SHEET_BELOW ? 'stage' : 'top-right';
}

/* ---- Bottom sheet ---------------------------------------------------- */

export const SHEET_SNAPS = ['peek', 'half', 'full'] as const;
export type SheetSnap = (typeof SHEET_SNAPS)[number];

/** Fractions of the stage column the sheet covers. Full leaves a sliver of the stage so it still reads as an overlay. */
export const SHEET_FRACTION: Record<SheetSnap, number> = { peek: 0.36, half: 0.6, full: 0.94 };
/** Peek never drops below this, so the header, the tabs and a few rows stay visible on short stages. */
export const SHEET_PEEK_MIN_PX = 200;
/** A drag that ends this far below the peek height closes the sheet. */
export const SHEET_CLOSE_DRAG_PX = 72;

export function sheetHeightPx(snap: SheetSnap, containerPx: number): number {
  const raw = containerPx * SHEET_FRACTION[snap];
  const full = containerPx * SHEET_FRACTION.full;
  if (snap === 'peek') return Math.round(Math.min(full, Math.max(SHEET_PEEK_MIN_PX, raw)));
  return Math.round(Math.min(full, Math.max(raw, sheetHeightPx('peek', containerPx))));
}

/** The snap a drag that ended at `heightPx` settles on, or 'closed' when it was pulled well below the peek. */
export function snapSheet(heightPx: number, containerPx: number): SheetSnap | 'closed' {
  const peek = sheetHeightPx('peek', containerPx);
  if (heightPx < peek - SHEET_CLOSE_DRAG_PX) return 'closed';
  let best: SheetSnap = 'peek';
  let bestGap = Infinity;
  for (const snap of SHEET_SNAPS) {
    const gap = Math.abs(sheetHeightPx(snap, containerPx) - heightPx);
    if (gap < bestGap) {
      best = snap;
      bestGap = gap;
    }
  }
  return best;
}

/** Keyboard and click steps of the drag handle: up grows, down shrinks, a click cycles. */
export function stepSheet(snap: SheetSnap, direction: 'up' | 'down' | 'cycle'): SheetSnap {
  const i = SHEET_SNAPS.indexOf(snap);
  if (direction === 'cycle') return SHEET_SNAPS[(i + 1) % SHEET_SNAPS.length];
  if (direction === 'up') return SHEET_SNAPS[Math.min(SHEET_SNAPS.length - 1, i + 1)];
  return SHEET_SNAPS[Math.max(0, i - 1)];
}

/* ---- Loader HUD ------------------------------------------------------ */

/** Below this stage width the secondary scene actions move into the overflow menu. */
export const HUD_OVERFLOW_BELOW = 1100;
/** Stage insets (12 px each side) plus the gap kept between the two groups. */
export const HUD_GUTTERS_PX = 36;

/** Scene actions that live in the top-right group on wide stages and in the overflow menu on narrow ones. */
export const HUD_SECONDARY_ACTIONS = ['mapStyle', 'catalogue', 'resetView', 'deepSpace'] as const;
export type HudSecondaryAction = (typeof HUD_SECONDARY_ACTIONS)[number];

export interface HudLayout {
  /** Secondary actions are in the overflow menu instead of inline. */
  overflow: boolean;
  /** The top-right group stacks vertically along the right edge because a row would reach the top-left pills. */
  vertical: boolean;
}

/**
 * `stageWidth` is the HUD's own width; `primaryWidth` the measured width of
 * the top-left row (network and window pills); `sceneRowWidth` the width the
 * top-right group takes laid out in a row (its controls' widths plus gaps and
 * padding, the same whichever way it is laid out). Neither measurement
 * depends on the layout chosen here, so the choice cannot oscillate.
 */
export function hudLayout(stageWidth: number, primaryWidth: number, sceneRowWidth: number): HudLayout {
  const overflow = stageWidth < HUD_OVERFLOW_BELOW;
  const vertical = overflow && primaryWidth + sceneRowWidth + HUD_GUTTERS_PX > stageWidth;
  return { overflow, vertical };
}

export function hudInlineActions(layout: HudLayout): readonly HudSecondaryAction[] {
  return layout.overflow ? [] : HUD_SECONDARY_ACTIONS;
}

export function hudMenuActions(layout: HudLayout): readonly HudSecondaryAction[] {
  return layout.overflow ? HUD_SECONDARY_ACTIONS : [];
}
